import { Panel } from "@/components/AppShell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NUM_FIELDS, rowErrors } from "@/components/upload/ReviewPanel";
import { PlayerPicker } from "@/components/upload/PlayerPicker";

const LABEL = { pts: "PTS", fgm: "FGM", fga: "FGA", fg3m: "3PM", fg3a: "3PA", ftm: "FTM", fta: "FTA", reb: "REB", ast: "AST", tov: "TO", stl: "STL", blk: "BLK", plus_minus: "+/-" };

const sum = (players, f) => players.reduce((a, p) => a + (Number(p[f]) || 0), 0);

export function TeamTable({ index, team, game, onChange }) {
  const setPlayer = (i, patch) => onChange({ players: team.players.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const removePlayer = (i) => onChange({ players: team.players.filter((_, j) => j !== i) });
  const setTotal = (f, v) => onChange({ totals: { ...team.totals, [f]: v === "" ? null : Number(v) } });
  const totalsBad = ["pts", "reb", "ast"].filter((f) => team.totals?.[f] != null && sum(team.players, f) !== Number(team.totals[f]));

  return (
    <Panel data-testid={`review-team-${index}`}>
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-800">
        <h3 className="font-display text-2xl font-bold uppercase">{team.name || `Team ${index + 1}`}</h3>
        <Select value={team.team_id || ""} onValueChange={(v) => onChange({ team_id: v })}>
          <SelectTrigger data-testid={`review-team-select-${index}`} className={`w-48 bg-slate-900/70 ${!team.team_id ? "cell-bad" : ""}`}><SelectValue placeholder="Match team" /></SelectTrigger>
          <SelectContent>
            {game && [[game.home_team_id, game.home_name], [game.away_team_id, game.away_name]].map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2 text-xs">
          {["pts", "reb", "ast"].map((f) => (
            <label key={f} className={`flex items-center gap-1 rounded px-2 py-1 border ${totalsBad.includes(f) ? "mismatch border-rose-500/70" : "border-slate-800"}`}>
              <span className="text-slate-400 uppercase">{f} total</span>
              <input data-testid={`team-${index}-total-${f}`} value={team.totals?.[f] ?? ""} onChange={(e) => setTotal(f, e.target.value)} className="w-12 bg-transparent font-mono-num text-right outline-none" />
              <span className={`font-mono-num ${totalsBad.includes(f) ? "text-rose-300" : "text-slate-500"}`}>Σ{sum(team.players, f)}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-900/90 text-slate-400 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Screenshot name</th>
              <th className="px-3 py-2 text-left">Match</th>
              <th className="px-2 py-2">MIN</th>
              {NUM_FIELDS.map((f) => <th key={f} className="px-1 py-2">{LABEL[f]}</th>)}
              <th />
            </tr>
          </thead>
          <tbody>
            {team.players.map((p, i) => {
              const err = rowErrors(p);
              const bad = err.missing.length || err.formula || err.unmatched;
              return (
                <tr key={i} data-testid={`review-row-${index}-${i}`} className={`border-t border-slate-800/70 ${bad ? "mismatch" : ""}`}>
                  <td className="px-3 py-1.5">
                    <input value={p.name || ""} onChange={(e) => setPlayer(i, { name: e.target.value })} className="bg-transparent outline-none w-36 font-semibold" data-testid={`row-name-${index}-${i}`} />
                    {err.formula && <div className="text-[10px] text-rose-300">PTS ≠ 2·FGM + 3PM + FTM ({2 * p.fgm + Number(p.fg3m) + Number(p.ftm)})</div>}
                  </td>
                  <td className="px-3 py-1.5"><PlayerPicker player={p} teamId={team.team_id} testId={`picker-${index}-${i}`} onChange={(patch) => setPlayer(i, patch)} /></td>
                  <td className="px-1 py-1.5"><input value={p.min ?? ""} onChange={(e) => setPlayer(i, { min: e.target.value })} className="w-14 rounded border border-slate-700 bg-slate-900/70 px-1 py-1 font-mono-num text-center" /></td>
                  {NUM_FIELDS.map((f) => (
                    <td key={f} className="px-1 py-1.5">
                      <input data-testid={`cell-${index}-${i}-${f}`} value={p[f] ?? ""} onChange={(e) => setPlayer(i, { [f]: e.target.value === "" ? null : e.target.value })}
                        className={`w-11 rounded border border-slate-700 bg-slate-900/70 px-1 py-1 font-mono-num text-center ${err.missing.includes(f) || (err.formula && f === "pts") ? "cell-bad" : ""}`} />
                    </td>
                  ))}
                  <td className="px-2"><button data-testid={`remove-row-${index}-${i}`} onClick={() => removePlayer(i)} className="text-slate-500 hover:text-rose-400">✕</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
